import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { Grid, TextField } from "@material-ui/core";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { Drawing } from "../../../../models/WorkOrderDrawing";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";

interface Props {
	workOrderDrawings: Drawing[];
	onSelectionChanged: (selectedDrawings: Drawing[]) => void;
}

export const AssetDrawingsTable = ({
	workOrderDrawings,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		state: { assetDrawings },
	} = useWorkOrderModalsContext();

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "DocumentName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);
	const rows = useMemo(() => {
		const uniqueDrawings = new Map();
		assetDrawings.forEach((drawing: any) => {
			const key = drawing.DocumentName;
			if (!uniqueDrawings.has(key)) {
				uniqueDrawings.set(key, {
					...drawing,
					id: key,
					isSelected: workOrderDrawings.some(
						(wd) =>
							wd.DocumentName === drawing.DocumentName && wd.IsAssetResource,
					),
					assets: [
						{
							name: drawing.AssetName,
							id: drawing.AssetId,
							path: drawing.FullPath,
						},
					],
				});
			} else {
				const existingDrawing = uniqueDrawings.get(key);
				existingDrawing.assets.push({
					name: drawing.AssetName,
					id: drawing.AssetId,
					path: drawing.FullPath,
				});
			}
		});
		return Array.from(uniqueDrawings.values());
	}, [assetDrawings, workOrderDrawings]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 33,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "DocumentName",
			headerName: "Document Name",
			flex: 3,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.DocumentName?.localeCompare(
						nodeB.data.DocumentName,
					);
				}
			},
		},
		{
			field: "Title",
			headerName: "Title",
			flex: 9,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Title?.localeCompare(nodeB.data.Title);
				}
			},
		},
	];

	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
		sortRows();
	}, [gridApi, workOrderDrawings]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};
	return (
		<>
			<Grid
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid item xs={12}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid>
			</Grid>
			<div
				className="ag-theme-alpine"
				style={{ width: "100%", height: "45vh" }}
			>
				<AgGridReact
					getRowId={(params) => params.data.id}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={defaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection="multiple"
					rowMultiSelectWithClick={true}
					onSelectionChanged={onAgGridSelectionChanged}
					suppressContextMenu={true}
					loading={isLoading}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
